// Client-side image compression using Canvas.
// - Resizes so max dimension ≤ 1920px
// - Re-encodes as JPEG (quality 0.85) if the result is smaller
// - Falls back to original file on any failure
// Returns a File-like object (or original file if compression not beneficial).

const MAX_DIMENSION = 1920;
const JPEG_QUALITY = 0.85;
const COMPRESS_THRESHOLD_BYTES = 1.5 * 1024 * 1024; // only compress if > 1.5MB

const readAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const loadImage = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });

export async function compressImageIfNeeded(file) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  if (file.size <= COMPRESS_THRESHOLD_BYTES) return file;

  try {
    const dataUrl = await readAsDataURL(file);
    const img = await loadImage(dataUrl);

    const { width, height } = img;
    const maxSide = Math.max(width, height);
    const scale = maxSide > MAX_DIMENSION ? MAX_DIMENSION / maxSide : 1;
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, targetW, targetH);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
    );
    if (!blob) return file;

    // Only use compressed version if actually smaller
    if (blob.size < file.size) {
      const newName = file.name.replace(/\.[^/.]+$/, '') + '.jpg';
      return new File([blob], newName, { type: 'image/jpeg' });
    }
    return file;
  } catch (e) {
    console.warn('[image-compress] failed, sending original:', e);
    return file;
  }
}
